import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { calendarsName } from '../../../lib/mocks/add-request-mock';
import { notificationsApi } from '../../services/notifications/notificationsApi';
import { requestDealsToggleData } from '../../../lib/mocks/clients-mock';

const initialState = {
  mainPage: {
    addedRequestCalendars: calendarsName,
    isSearch: false,
    searchType: '',
    searchQuery: '',
    searchQueryRequest: '',
    totalSearchResults: 0,
    searchFilters: '',
    searchSortBy: '',
    searchRequestId: null,
    searchLikeIds: null,
    searchLikeFilter: null, // null | 'like' | 'dislike'
    lastSearchValues: null, // Formik values from the last submitted search — enables form re-hydration on remount
    showMap: false,
    favsRealties: [],
    shouldResetFilters: false,
  },

  clientsPage: {
    toggleList: requestDealsToggleData,
  },

  selectedCountryId: 73,
  cities: [],
  citiesLoading: false,
  citiesError: null,
  citiesSelect: [],
  citiesSelectLoading: false,
  citiesSelectError: null,
  districts: [],
  districtsLoading: false,
  districtsError: null,

  streetsSearch: [],
  streetsSearchLoading: false,
  streets: [],
  streetsLoading: false,
  streetsError: null,
  streetsNumbers: [],
  streetsNumbersLoading: false,
  streetsNumbersError: null,

  residences: [],
  residencesLoading: false,
  residencesSearch: [],
  residencesSearchLoading: false,
  residencesError: null,

  notificationsUnread: null,
};

// setSelectCities is the same as setCities, needed when we have 2 selects
// in one form, cuz data must be in redux
export const setCities = createAsyncThunk(
  'dashboard/setCities',
  async payload => payload
);

export const setSelectCities = createAsyncThunk(
  'dashboard/setSelectCities',
  async payload => payload
);

export const setDistricts = createAsyncThunk(
  'dashboard/setDistricts',
  async payload => payload
);

export const setStreets = createAsyncThunk(
  'dashboard/setStreets',
  async payload => payload
);

export const setStreetsSearch = createAsyncThunk(
  'dashboard/setStreetsSearch',
  async payload => payload
);

export const filterStreetsSearch = createAsyncThunk(
  'dashboard/filterStreetsSearch',
  async payload => payload
);

export const setStreetsNumbers = createAsyncThunk(
  'dashboard/setStreetsNumbers',
  async payload => payload
);

export const setResidences = createAsyncThunk(
  'dashboard/setResidences',
  async payload => payload
);

export const setResidencesSearch = createAsyncThunk(
  'dashboard/setResidencesSearch',
  async payload => payload
);

export const filterResidencesSearch = createAsyncThunk(
  'dashboard/filterResidencesSearch',
  async payload => payload
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,

  reducers: {
    addRequestCalendar: state =>
      void (state.mainPage.addedRequestCalendars = [
        ...state.mainPage.addedRequestCalendars.slice(1),
      ]),
    deleteRequestCalendar: (state, action) =>
      void (state.mainPage.addedRequestCalendars = [
        ...state.mainPage.addedRequestCalendars,
        action.payload,
      ]),
    showSearch: (state, action) => {
      const p = action.payload ?? {};
      if ('isSearchVisible' in p) state.mainPage.isSearch = p.isSearchVisible;
      if ('searchType' in p) state.mainPage.searchType = p.searchType;
      if ('searchQuery' in p) state.mainPage.searchQuery = p.searchQuery;
      if ('searchQueryRequest' in p)
        state.mainPage.searchQueryRequest = p.searchQueryRequest;
      if ('searchFilters' in p) state.mainPage.searchFilters = p.searchFilters;
      if ('searchLikeIds' in p) state.mainPage.searchLikeIds = p.searchLikeIds;
      if ('searchLikeFilter' in p)
        state.mainPage.searchLikeFilter = p.searchLikeFilter;
      if ('lastSearchValues' in p)
        state.mainPage.lastSearchValues = p.lastSearchValues;
    },
    setTotalSearchResults: (state, action) => {
      state.mainPage.totalSearchResults = action.payload;
    },
    setSortBy: (state, action) => {
      state.mainPage.searchSortBy = action.payload;
    },
    setSearchRequestId: (state, action) => {
      state.mainPage.searchRequestId = action.payload;
    },
    setShowMap: (state, action) => {
      state.mainPage.showMap = action.payload;
    },
    setFavsRealties: (state, action) => {
      state.mainPage.favsRealties = action.payload;
    },
    setClientsToggleList: (state, action) => {
      state.clientsPage.toggleList = action.payload;
    },
    setShouldResetFilters: (state, action) => {
      state.mainPage.shouldResetFilters = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(setCities.pending, (state, action) => {
      state.citiesLoading = true;
    });
    builder.addCase(setCities.fulfilled, (state, action) => {
      state.cities = action.payload;
      state.citiesLoading = false;
    });
    builder.addCase(setSelectCities.pending, (state, action) => {
      state.citiesSelectLoading = true;
    });
    builder.addCase(setSelectCities.fulfilled, (state, action) => {
      state.citiesSelect = action.payload;
      state.citiesSelectLoading = false;
    });

    builder.addCase(setDistricts.pending, (state, action) => {
      state.districtsLoading = true;
    });
    builder.addCase(setDistricts.fulfilled, (state, action) => {
      state.districts = action.payload;
      state.districtsLoading = false;
    });

    builder.addCase(setStreets.pending, (state, action) => {
      state.streetsLoading = true;
    });
    builder.addCase(setStreets.fulfilled, (state, action) => {
      state.streets = action.payload;
      state.streetsLoading = false;
    });
    builder.addCase(setStreetsSearch.pending, (state, action) => {
      state.streetsSearchLoading = true;
    });
    builder.addCase(setStreetsSearch.fulfilled, (state, action) => {
      const newStreets = action.payload;

      const hasCurrentCity = state.streetsSearch.some(
        street => street.city_id.id === newStreets[0]?.city_id.id
      );

      if (!hasCurrentCity) {
        state.streetsSearch = [...state.streetsSearch, ...action.payload];
      }

      state.streetsSearchLoading = false;
    });
    builder.addCase(filterStreetsSearch.fulfilled, (state, action) => {
      const id = action.payload;

      if (!id || id?.length < 1) {
        state.streetsSearch = [];
      } else {
        state.streetsSearch = state.streetsSearch.filter(
          street => street.city_id.id !== id
        );
      }
    });

    builder.addCase(setStreetsNumbers.pending, (state, action) => {
      state.streetsNumbersLoading = true;
    });
    builder.addCase(setStreetsNumbers.fulfilled, (state, action) => {
      state.streetsNumbers = action.payload;
      state.streetsNumbersLoading = false;
    });

    builder.addCase(setResidences.pending, (state, action) => {
      state.residencesLoading = true;
    });
    builder.addCase(setResidences.fulfilled, (state, action) => {
      state.residences = action.payload;
      state.residencesLoading = false;
    });
    builder.addCase(setResidencesSearch.pending, (state, action) => {
      state.residencesSearchLoading = true;
    });
    builder.addCase(setResidencesSearch.fulfilled, (state, action) => {
      const newResidences = action.payload;
      const hasCurrentCity = state.residencesSearch.some(
        residence => residence.city_id.id === newResidences[0]?.city_id.id
      );

      if (!hasCurrentCity) {
        state.residencesSearch = [...state.residencesSearch, ...action.payload];
      }

      state.residencesSearchLoading = false;
    });
    builder.addCase(filterResidencesSearch.fulfilled, (state, action) => {
      const id = action.payload;
      if (!id || id?.length < 1) {
        state.residencesSearch = [];
      } else {
        state.residencesSearch = state.residencesSearch.filter(
          residence => residence.city_id.id !== id
        );
      }
    });

    builder.addMatcher(
      notificationsApi.endpoints.getNotifications.matchPending,
      (state, action) => {
        state.notificationsLoading = true;
      }
    );
    builder.addMatcher(
      notificationsApi.endpoints.getNotifications.matchFulfilled,
      (state, action) => {
        state.notificationsError = null;

        if (action.payload?.meta?.unread_count) {
          const unreadCount = action.payload.meta.unread_count;

          state.notificationsUnread = unreadCount;
        } else {
          state.notificationsUnread = null;
        }

        state.notificationsLoading = false;
      }
    );
    builder.addMatcher(
      notificationsApi.endpoints.getNotifications.matchRejected,
      (state, action) => {
        console.log('Error in fetching notifications: ', action);
        state.notificationsError = 'Error in fetching notifications';
        state.notificationsLoading = false;
      }
    );

    builder.addMatcher(
      notificationsApi.endpoints.readAllNotifications.matchFulfilled,
      (state, action) => {
        state.notificationsUnread = null;
      }
    );

    /* builder.addMatcher(
      citiesApi.endpoints.getCitiesForCountry.matchFulfilled,
      (state, action) => {
        state.citiesError = null;
        state.cities = action.payload;
      }
    );
    builder.addMatcher(
      citiesApi.endpoints.getCitiesForCountry.matchRejected,
      (state, action) => {
        console.log('Error in fetching cities: ', action);
        state.citiesError = 'Error in fetching cities';
      }
    );

    builder.addMatcher(
      districtsApi.endpoints.getDistrictsForCity.matchFulfilled,
      (state, action) => {
        state.districtsError = null;
        state.districts = action.payload;
      }
    );
    builder.addMatcher(
      districtsApi.endpoints.getDistrictsForCity.matchRejected,
      (state, action) => {
        console.log('Error in fetching districts: ', action);
        state.districtsError = 'Error in fetching districts';
      }
    );

    builder.addMatcher(
      streetsApi.endpoints.getStreetsForCity.matchFulfilled,
      (state, action) => {
        state.streetsError = null;
        state.streets = action.payload;
      }
    );
    builder.addMatcher(
      streetsApi.endpoints.getStreetsForCity.matchRejected,
      (state, action) => {
        console.log('Error in fetching streets: ', action);
        state.streetsError = 'Error in fetching streets';
      }
    );

    builder.addMatcher(
      streetsNumberApi.endpoints.getStreetsNumberForStreet.matchFulfilled,
      (state, action) => {
        state.streetsNumbersError = null;
        state.streetsNumbers = action.payload;
      }
    );
    builder.addMatcher(
      streetsNumberApi.endpoints.getStreetsNumberForStreet.matchRejected,
      (state, action) => {
        console.log('Error in fetching streetsNumbers: ', action);
        state.streetsNumbersError = 'Error in fetching streetsNumbers';
      }
    );

    builder.addMatcher(
      residencesApi.endpoints.getResidencesForCity.matchFulfilled,
      (state, action) => {
        state.residencesError = null;
        state.residences = action.payload;
      }
    );
    builder.addMatcher(
      residencesApi.endpoints.getResidencesForCity.matchRejected,
      (state, action) => {
        console.log('Error in fetching residences: ', action);
        state.residencesError = 'Error in fetching residences';
      }
    ); */
  },
});

export const {
  addRequestCalendar,
  deleteRequestCalendar,
  showSearch,
  setTotalSearchResults,
  setSortBy,
  setSearchRequestId,
  setShowMap,
  setClientsToggleList,
  setFavsRealties,
  setShouldResetFilters,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
